import { useIsWebviewMode } from 'src/Hooks'
import { useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

const NO_REFRESH_INDICATOR = '#no-refresh'
const IGNORE_PATHS = ['/login']

/* Shared by all hook instances: several drawers/modals can be open at once (e.g. stacked ai360 details),
 * so the hash must only be added by the first one to open and removed once the last one closes. */
let activeReloadPreventers = 0
let originalFragment = ''

const hasNoRefreshHash = (): boolean => Boolean(globalThis?.location?.hash?.includes(NO_REFRESH_INDICATOR))

/**
 * This hooks prevents the native reload on mobile apps.
 * Useful if a scroll event is used on webpage which triggers the native reload (which is not intended)
 *
 * Native reload: Swipe down the screen to reload page
 *
 * Safe to call from several components at the same time (e.g. stacked drawers) - a shared counter tracks
 * how many callers currently want the hash, so a closing caller never clears it out from under another one.
 */
export const usePreventReloadOnWebview = (isDrawerOpen: boolean): void => {
  const navigate = useNavigate()
  const location = useLocation()
  const isWebview = useIsWebviewMode()
  const isRegisteredRef = useRef(false)

  useEffect(() => {
    if (!isWebview) return
    if (IGNORE_PATHS.includes(location.pathname)) return

    if (isDrawerOpen && !isRegisteredRef.current) {
      isRegisteredRef.current = true
      activeReloadPreventers += 1
      if (activeReloadPreventers === 1) {
        originalFragment = globalThis.location.hash === NO_REFRESH_INDICATOR ? '' : globalThis.location.hash
        navigate(
          {
            pathname: location.pathname,
            search: location.search,
            hash: NO_REFRESH_INDICATOR,
          },
          { replace: true }
        )
      }
    }

    return () => {
      if (!isRegisteredRef.current) return
      isRegisteredRef.current = false
      activeReloadPreventers = Math.max(0, activeReloadPreventers - 1)

      /* Only the last remaining caller may clear the hash, and only if the user hasn't already navigated elsewhere */
      if (activeReloadPreventers > 0) return

      /* there are other links with hash like #wallbox-settings, which should not be removed when using navigate({ replace: true }) */
      const fragmentToRestore = originalFragment
      originalFragment = ''
      if (!hasNoRefreshHash()) return

      const markedPath = location.pathname.replace(NO_REFRESH_INDICATOR, '').replace(/\/$/, '')
      const currentPath = globalThis?.location?.pathname?.replace(NO_REFRESH_INDICATOR, '').replace(/\/$/, '')
      if (markedPath === currentPath) {
        navigate(
          {
            pathname: markedPath,
            search: globalThis.location.search,
            hash: fragmentToRestore,
          },
          { replace: true }
        )
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isWebview, location.pathname, navigate])
}
